import _ from 'lodash';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {CardModel} from '../../../models/api/cardModel';
import {
  addCardGroupToCardAsyncAction,
  removeCardGroupFromCardAsyncAction,
} from '../../../store/data/cardGroups/cardGroupsActions';
import {DropdownCategory} from '../../common/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../common/dropdowns/dropdownEmpty';
import {DropdownItemAccentIcon} from '../../common/dropdowns/dropdownItemAccentIcon';
import {DropdownLoader} from '../../common/dropdowns/dropdownLoader';
import {DeprecatedSearchDropdown} from '../../common/dropdowns/search/deprecatedSearchDropdown';
import {TypedRowValue} from '../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../common/dropdowns/source/syncDropdownSource';
import {useContactDetailsContext} from '../contactDetailsContext';
import {
  buildContactDetailsEditCardGroupEvent,
  ContactDetailsCardGroupActionsEnum,
} from '../contactDetailsTrackingHelpers';

/*
 * Constants.
 */

const DropdownMaxheight = 340;

enum AddToGroupDropdownTypesEnum {
  CATEGORY = 'CATEGORY',
  NEW_ACTION = 'NEW_ACTION',
  GROUP = 'GROUP',
}

/*
 * Props.
 */

interface ContactHeaderAddToGroupDropdownProps {
  popoverProps: PopoverTransferProps;
  card: CardModel;
  fetchCardGroupsAsync: AsyncHandler<ReadonlyArray<CardGroupModel>>;
  onNewGroupClick: Handler;
}

interface StyledTooltipOverflowConditionProps {
  $isSelected: boolean;
}

type AddToGroupDropdownRowTypedValue =
  | TypedRowValue<AddToGroupDropdownTypesEnum.CATEGORY, ReactNode>
  | TypedRowValue<AddToGroupDropdownTypesEnum.NEW_ACTION, ReactNode>
  | TypedRowValue<AddToGroupDropdownTypesEnum.GROUP, CardGroupModel>;

/*
 * Styles.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-flow: row;
  overflow: hidden;
`;

const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)<StyledTooltipOverflowConditionProps>`
  direction: ltr;

  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledDropdownItemAccentIcon = styled(DropdownItemAccentIcon)`
  padding: 4px;
`;

/*
 * Component.
 */

export const ContactHeaderAddToGroupDropdown: FC<ContactHeaderAddToGroupDropdownProps> = (props) => {
  const {popoverProps, card, fetchCardGroupsAsync, onNewGroupClick} = props;
  const [groups, setGroups] = useState<ReadonlyArray<CardGroupModel>>([]);
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const [isSearching, setIsSearching] = useState(false);
  const dispatch = useAppDispatch();
  const {onTrackEvent} = useContactDetailsContext();

  /** Fetch the groups and set the proper status */
  const fetchGroups = useCallback(async () => {
    setStatus(AsyncStatusesEnum.LOADING);

    try {
      const fetchedGroups = await fetchCardGroupsAsync();
      // Order the fetched card groups with checked items first
      const orderedGroups = _.partition(fetchedGroups, (group) => card.groupIds.includes(group.id));
      setGroups([...orderedGroups[0], ...orderedGroups[1]]);
      setStatus(AsyncStatusesEnum.LOADED);
    } catch (err) {
      setStatus(AsyncStatusesEnum.FAILED);
    }
  }, [setGroups, fetchCardGroupsAsync, setStatus, card]);

  useInitialEffect(() => {
    fetchGroups();
  });

  /** Dropdown Actions */

  const onRemove = useCallback(
    (group: CardGroupModel) => {
      dispatch(removeCardGroupFromCardAsyncAction(group, card.id));
      if (onTrackEvent) {
        onTrackEvent(
          buildContactDetailsEditCardGroupEvent(ContactDetailsCardGroupActionsEnum.DELETE, card, group),
        );
      }
    },
    [dispatch, onTrackEvent, card],
  );

  const onAdd = useCallback(
    (group: CardGroupModel) => {
      dispatch(addCardGroupToCardAsyncAction(group, card.id));
      if (onTrackEvent) {
        onTrackEvent(
          buildContactDetailsEditCardGroupEvent(ContactDetailsCardGroupActionsEnum.ADD, card, group),
        );
      }
    },
    [dispatch, onTrackEvent, card],
  );

  /** Dropdown Setup */

  const onSearchQueryChange = (query: string) => {
    setIsSearching(Boolean(query));
  };

  const dropdownSource = new SyncDropdownSource<AddToGroupDropdownRowTypedValue>({
    items: getDropdownItems(status, groups, isSearching, onNewGroupClick),
    rowFilter,
    renderRow: (item: AddToGroupDropdownRowTypedValue) => renderRow(item, card.groupIds, onRemove, onAdd),
    renderEmpty: (_query: string) => renderEmptyOrLoading(status, fetchGroups),
  });

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={<Trans id="contacts-header-group-add-dropdown-title">Lists</Trans>}
      maxHeight={DropdownMaxheight}
      onSearchQueryChange={onSearchQueryChange}
      source={dropdownSource}
      shouldIncludeSearchIcon
    />
  );
};

const renderEmptyOrLoading = (status: AsyncStatusesEnum, onRetryClick: Handler) => {
  if (status === AsyncStatusesEnum.LOADED) {
    return <DropdownEmpty />;
  }

  return <DropdownLoader size={VisualSizesEnum.MEDIUM} status={status} onRetryClick={onRetryClick} />;
};

const rowFilter = (items: ReadonlyArray<AddToGroupDropdownRowTypedValue>, query: string) => {
  if (!query) {
    return items;
  }

  const queryInLowerCase = query.toLowerCase();
  const filteredGroups = items.filter(
    (item) =>
      item.type === AddToGroupDropdownTypesEnum.GROUP &&
      item.value.name.toLowerCase().includes(queryInLowerCase),
  );

  if (filteredGroups.length === 0) {
    return [];
  }
  return [...items.filter((item) => item.type === AddToGroupDropdownTypesEnum.CATEGORY), ...filteredGroups];
};

const getDropdownItems = (
  status: AsyncStatusesEnum,
  groups: ReadonlyArray<CardGroupModel>,
  isSearching: boolean,
  onNewGroupClick: Handler,
): ReadonlyArray<AddToGroupDropdownRowTypedValue> => {
  // If the items are loading or errors, return nothing and let the empty handler display.
  if (status !== AsyncStatusesEnum.LOADED) {
    return [];
  }

  const shouldDisplayCreateHeaderAndAction = !isSearching;
  const shouldDisplayAllGroupsHeader = groups.length > 0;
  const createHeaderAndAction = [
    buildDropdownCategory(<Trans id="contacts-header-group-add-dropdown-create-header">Create</Trans>),
    buildDropdownNewGroupAction(onNewGroupClick),
  ];
  const allGroupsHeader = buildDropdownCategory(
    <Trans id="contacts-header-group-add-dropdown-all-groups-header">All lists</Trans>,
  );

  return _.compact([
    ...(shouldDisplayCreateHeaderAndAction ? createHeaderAndAction : []),
    shouldDisplayAllGroupsHeader ? allGroupsHeader : undefined,
    ...groups.map(buildDropdownGroup),
  ]);
};

const buildDropdownCategory = (value: React.ReactNode): AddToGroupDropdownRowTypedValue => ({
  type: AddToGroupDropdownTypesEnum.CATEGORY,
  value: <DropdownCategory>{value}</DropdownCategory>,
});

const buildDropdownNewGroupAction = (onNewGroupClick: Handler): AddToGroupDropdownRowTypedValue => ({
  type: AddToGroupDropdownTypesEnum.NEW_ACTION,
  value: (
    <DropdownItem
      interactionId="contact-details-group-dropdown-new-group"
      onSelect={(event) => {
        event.preventDefault();
        onNewGroupClick();
      }}
    >
      <StyledDropdownItemAccentIcon label={null} iconName="newTiny" />
      <DropdownItemContent>
        <Trans id="contacts-header-group-add-dropdown-new-group-action">New list</Trans>
      </DropdownItemContent>
    </DropdownItem>
  ),
});

const buildDropdownGroup = (value: CardGroupModel): AddToGroupDropdownRowTypedValue => ({
  type: AddToGroupDropdownTypesEnum.GROUP,
  value,
});

const renderRow = (
  item: AddToGroupDropdownRowTypedValue,
  alreadyAddedGroupIds: ReadonlyArray<number>,
  onRemove: HandlerOf<CardGroupModel>,
  onAdd: HandlerOf<CardGroupModel>,
) => {
  if (item.type !== AddToGroupDropdownTypesEnum.GROUP) {
    return item.value;
  }

  const group = item.value;
  const isSelected = alreadyAddedGroupIds.includes(group.id);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <TooltipCoordinator render={() => group.name} placement="top">
          <DropdownItem
            interactionId={`contact-details-group-${group.id}`}
            onSelect={(event) => {
              // Do not close the dropdown
              event.preventDefault();
              if (isSelected) {
                onRemove(group);
              } else {
                onAdd(group);
              }
            }}
          >
            <StyledDropdownItemContent>
              <StyledTooltipOverflowCondition $isSelected={isSelected}>
                {group.name}
              </StyledTooltipOverflowCondition>
              {isTeammateNamespace(group.namespace) && (
                <StyledIcon name="lockFilledNewTiny" color={theme.greys.shade70} />
              )}
            </StyledDropdownItemContent>
            <DropdownItemCheckbox isChecked={isSelected} />
          </DropdownItem>
        </TooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
};
